import React, { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Easing, Text, TouchableOpacity, View } from "react-native";
import { observer } from "mobx-react";
import { AntDesign } from "@expo/vector-icons";
import Colors from "../../Resources/Color";
import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../_layout";
import ExerciseSearchFloating from "../Exercise/ExerciseSearchFloating";

/**
 * Helper function to determine suggestion context based on protocol slot
 * @param {string} protocolSlot - The protocol slot value (e.g., "primarySquat-string")
 * @returns {string|null} - The suggestion context ("squat-string", "benchPress-string", "deadlift-string", or null)
 */
const getSuggestionContext = (protocolSlot) => {
  if (!protocolSlot) return null;

  if (protocolSlot.toLowerCase().includes("squat")) {
    return "squat";
  } else if (protocolSlot.toLowerCase().includes("benchpress")) {
    return "benchPress";
  } else if (protocolSlot.toLowerCase().includes("deadlift")) {
    return "deadlift";
  }

  return null;
};

/**
 * SlotTextNew
 *
 * Slot component with a clean '+' button that opens ExerciseSearchFloating
 * for exercise/modifier search.
 *
 * Props:
 * - slotExercise: the slot exercise object (can be null)
 * - selectSlot: function to handle slot selection
 * - useCTAColors: boolean for CTA color styling
 * - calendar: boolean indicating if in calendar view
 * - block: the block object
 * - protocolTitle: the protocol title string (e.g., "primarySquat-string")
 * - week: the week number for this slot
 */
const SlotTextNew = observer(
  ({
    slotExercise,
    selectSlot,
    useCTAColors,
    calendar,
    block,
    protocolTitle,
    week,
    onRegisterOpenSearch,
    onExerciseSetFinished,
  }) => {
    const { t, i18n } = useTranslation();
    const { screenSize } = useScreenDimensions();
    const { isMobile } = useDevice();

    const [isHovered, setIsHovered] = useState(false);
    const [isPlusHovered, setIsPlusHovered] = useState(false);

    const buttonRef = useRef(null);
    const secondAddButtonRef = useRef(null);

    // Stable per-component id for empty slots so multiple empty slots don't share the same instanceId
    const emptySlotLocalIdRef = useRef(
      `empty-${Math.random().toString(36).slice(2, 10)}`
    );

    // Generate a stable instance ID for this slot
    const instanceId = useMemo(() => {
      const blockId = block?.id || "unknown";
      const slotKey = slotExercise?.id || emptySlotLocalIdRef.current;
      return `slot-search-${blockId}-${slotKey}`;
    }, [block?.id, slotExercise?.id]);

    // Determine suggestion context based on protocol slot
    // Use protocolTitle (from parent ProtocolRow) instead of slotExercise.protocolSlot
    // because slotExercise is null when the slot is empty
    const suggestionContext = useMemo(() => {
      const context = getSuggestionContext(protocolTitle);
      return context;
    }, [protocolTitle]);

    // Get coach store
    const coach = block?.client?.coach ?? block?.coach;
    const isSearchOpen = coach?.activeFloatingSearchId === instanceId;
    const isSecondSearchOpen =
      coach?.activeFloatingSearchId === `${instanceId}-second`;

    // Refs for the exercise text buttons
    const primaryExerciseRef = useRef(null);
    const secondExerciseRef = useRef(null);

    const secondProtocolReferenceElement = slotExercise?.getSecondProtocol
      ? secondExerciseRef.current
      : secondAddButtonRef.current;

    const shouldShowSecondAddButton =
      ((isHovered || isSecondSearchOpen) && !isSearchOpen) || isMobile;

    // On mobile we always want the second-protocol '+' to be visible.
    // Initialize the animated value accordingly to avoid a hide->show flash
    // when components remount (common during slot selection updates).
    const secondAddButtonAnim = useRef(
      new Animated.Value(isMobile ? 1 : 0)
    ).current;

    useEffect(() => {
      // Keep the '+' always rendered/visible on mobile (no delayed animation).
      if (isMobile) {
        secondAddButtonAnim.stopAnimation();
        secondAddButtonAnim.setValue(1);
        return;
      }

      Animated.timing(secondAddButtonAnim, {
        toValue: shouldShowSecondAddButton ? 1 : 0,
        delay: 400,
        duration: 200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    }, [isMobile, secondAddButtonAnim, shouldShowSecondAddButton]);

    const handleSelectExercise = (item) => {
      if (!block || !item) {
        handleCloseSearch();
        return;
      }

      // Determine if we're working with a second protocol by checking the active search ID
      const isSecondProtocol =
        coach?.activeFloatingSearchId?.endsWith("-second");

      if (slotExercise) {
        if (isSecondProtocol) {
          // Working with second protocol
          if (slotExercise.getSecondProtocol) {
            // Update existing second protocol
            block.updateSlotFromSearchResult(
              slotExercise.getSecondProtocol,
              item,
              t,
              true
            );
          } else {
            // Create new second protocol on the primary exercise
            block.addSlotFromSearchResult(
              item,
              t,
              protocolTitle,
              week,
              true,
              slotExercise
            );
          }
        } else {
          // Update existing primary slot exercise with new selection
          block.updateSlotFromSearchResult(slotExercise, item, t, false);
        }
      } else {
        // Create new primary slot with the selected exercise and modifiers
        block.addSlotFromSearchResult(
          item,
          t,
          protocolTitle,
          week,
          false,
          null
        );
      }

      handleCloseSearch();

      // Let parent know we've finished setting the exercise (so it can gate focus).
      try {
        onExerciseSetFinished?.({ isSecondProtocol: !!isSecondProtocol });
      } catch (e) {
        // no-op
      }
    };

    const handleOpenSearch = (forSecondProtocol = false) => {
      if (coach) {
        setIsHovered(false);
        setIsPlusHovered(false);
        // Close any currently open search (if it's a different search)
        const searchInstanceId = forSecondProtocol
          ? `${instanceId}-second`
          : instanceId;

        if (
          coach.floatingSearchCloseCallback &&
          coach.activeFloatingSearchId !== searchInstanceId
        ) {
          coach.floatingSearchCloseCallback();
        }

        // Open this search
        coach.setProperty("activeFloatingSearchId", searchInstanceId);
        coach.setProperty("floatingSearchCloseCallback", handleCloseSearch);

        // Store initial search text for the floating search
        if (slotExercise) {
          const initialText = forSecondProtocol
            ? slotExercise.getSecondProtocol?.getName(t, i18n.language) || ""
            : slotExercise.getName(t, i18n.language);
          coach.setProperty("floatingSearchInitialText", initialText);
        } else {
          coach.setProperty("floatingSearchInitialText", "");
        }
      }
    };

    useEffect(() => {
      if (onRegisterOpenSearch) {
        onRegisterOpenSearch(handleOpenSearch);
        return () => onRegisterOpenSearch(null);
      }
    }, [handleOpenSearch, onRegisterOpenSearch]);

    const handleCloseSearch = () => {
      if (coach) {
        setIsHovered(false);
        setIsPlusHovered(false);
        coach.setProperty("activeFloatingSearchId", null);
        coach.setProperty("floatingSearchCloseCallback", null);
        coach.setProperty("floatingSearchInitialText", "");
      }
    };

    // If this view unmounts (e.g., user navigates away), ensure any floating
    // search owned by this slot is closed. This prevents FloatingUI from
    // rendering at (0,0) with a stale/missing reference element.
    useEffect(() => {
      return () => {
        if (!coach) {
          return;
        }

        const activeId = coach.activeFloatingSearchId;
        const ownsActiveId =
          activeId === instanceId || activeId === `${instanceId}-second`;

        if (ownsActiveId) {
          coach.setProperty("activeFloatingSearchId", null);
          coach.setProperty("floatingSearchCloseCallback", null);
          coach.setProperty("floatingSearchInitialText", "");
        }
      };
    }, [coach, instanceId]);

    return (
      <View
        style={{
          alignItems: "center",
          flex: 1,
        }}
        onMouseEnter={() => {
          if (!isMobile) {
            setIsHovered(true);
          }
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        {slotExercise ? (
          <>
            <TouchableOpacity
              ref={primaryExerciseRef}
              onPress={() => handleOpenSearch(false)}
              style={{
                width: "100%",
                outlineStyle: "none",
              }}
            >
              <Text
                numberOfLines={2}
                style={{
                  width: "100%",
                  color: isSearchOpen ? Colors.bubbleColor : "black",
                  textAlign: "center",
                  fontSize: screenSize === "small" ? 11 : 11,
                  padding: 2,
                }}
              >
                {slotExercise.getName(t, i18n.language)}
              </Text>
            </TouchableOpacity>

            {/* Primary exercise floating search */}
            <ExerciseSearchFloating
              isOpen={isSearchOpen}
              onClose={handleCloseSearch}
              referenceElement={primaryExerciseRef.current}
              onSelectExercise={handleSelectExercise}
              instanceId={instanceId}
              suggestionContext={suggestionContext}
              initialSearchText={coach?.floatingSearchInitialText || ""}
              slotExercise={slotExercise}
              primarySlotExercise={slotExercise}
              block={block}
              protocolSlot={protocolTitle}
              currentWeek={week}
            />

            {slotExercise.getSecondProtocol && (
              <>
                <Text
                  style={{
                    width: "100%",
                    color: "black",
                    textAlign: "center",
                    fontSize: screenSize === "small" ? 11 : 11,
                    padding: 2,
                  }}
                >
                  +
                </Text>
                <TouchableOpacity
                  ref={secondExerciseRef}
                  onPress={() => handleOpenSearch(true)}
                  style={{
                    width: "100%",
                    outlineStyle: "none",
                  }}
                >
                  <Text
                    numberOfLines={2}
                    style={{
                      width: "100%",
                      color: isSecondSearchOpen ? Colors.bubbleColor : "black",
                      textAlign: "center",
                      fontSize: screenSize === "small" ? 11 : 11,
                      padding: 2,
                    }}
                  >
                    {slotExercise.getSecondProtocol.getName(t, i18n.language)}
                  </Text>
                </TouchableOpacity>
              </>
            )}

            {/* Second protocol floating search (anchored to text or add '+') */}
            <ExerciseSearchFloating
              isOpen={isSecondSearchOpen}
              onClose={handleCloseSearch}
              referenceElement={secondProtocolReferenceElement}
              onSelectExercise={handleSelectExercise}
              instanceId={`${instanceId}-second`}
              suggestionContext={suggestionContext}
              initialSearchText={coach?.floatingSearchInitialText || ""}
              slotExercise={slotExercise?.getSecondProtocol || slotExercise}
              primarySlotExercise={slotExercise}
              block={block}
              protocolSlot={protocolTitle}
              currentWeek={week}
            />

            {!slotExercise.getSecondProtocol && (
              <Animated.View
                pointerEvents={shouldShowSecondAddButton ? "auto" : "none"}
                style={{
                  width: "50%",
                  overflow: "hidden",
                  height: secondAddButtonAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 16],
                  }),
                  marginTop: secondAddButtonAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, 5],
                  }),
                  opacity: secondAddButtonAnim,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TouchableOpacity
                  ref={secondAddButtonRef}
                  onPress={() => {
                    handleOpenSearch(true);
                    setIsHovered(false);
                  }}
                  style={{
                    width: "100%",
                    alignItems: "center",
                    backgroundColor: isSecondSearchOpen
                      ? Colors.bubbleColor
                      : Colors.lightGrayColor,
                    borderRadius: 10,
                    outlineStyle: "none",
                  }}
                >
                  <AntDesign
                    name="plus"
                    size={8}
                    color={
                      isSecondSearchOpen ? Colors.whiteColor : Colors.blackColor
                    }
                  />
                </TouchableOpacity>
              </Animated.View>
            )}
          </>
        ) : (
          <View
            style={{
              width: "100%",
              maxWidth: 128,
            }}
          >
            <TouchableOpacity
              ref={buttonRef}
              onPress={() => handleOpenSearch(false)}
              style={{
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  isSearchOpen || useCTAColors
                    ? Colors.bubbleColor
                    : Colors.lightGrayColor,
                borderRadius: 10,
                // paddingVertical: 8,
                // paddingHorizontal: 8,
                outlineStyle: "none",
                opacity: isPlusHovered ? 0.8 : 1,
                // height: 8,
              }}
              onMouseEnter={() => !isMobile && setIsPlusHovered(true)}
              onMouseLeave={() => setIsPlusHovered(false)}
            >
              <AntDesign
                name="plus"
                size={10}
                color={
                  isSearchOpen || useCTAColors
                    ? Colors.whiteColor
                    : Colors.blackColor
                }
              />
            </TouchableOpacity>

            {/* Floating search component */}
            <ExerciseSearchFloating
              isOpen={isSearchOpen}
              onClose={handleCloseSearch}
              referenceElement={buttonRef.current}
              onSelectExercise={handleSelectExercise}
              instanceId={instanceId}
              suggestionContext={suggestionContext}
              initialSearchText={coach?.floatingSearchInitialText || ""}
              slotExercise={slotExercise}
              primarySlotExercise={slotExercise}
              block={block}
              protocolSlot={protocolTitle}
              currentWeek={week}
            />
          </View>
        )}
      </View>
    );
  }
);

export default SlotTextNew;
